import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { API_URL } from "../../components/api";
import { formatDateTime } from "../../../utils/dateFormat";
import LanguageContext from "../../context/LanguageContext";
import "./CollectionRequests.css";

const errorMessage = (error, fallback) =>
  error.response?.data?.message ||
  error.response?.data?.error ||
  fallback;

const authHeader = () => {
  const authTokens = JSON.parse(localStorage.getItem("authTokens"));
  return { Authorization: `Bearer ${authTokens?.access}` };
};

const CollectionRequests = () => {
  const { direction, t } = useContext(LanguageContext);
  const isArabic = document.documentElement.lang === "ar";

  const [collectors, setCollectors] = useState([]);
  const [requests, setRequests] = useState([]);
  const [collectorId, setCollectorId] = useState("");
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [processingId, setProcessingId] = useState(null);
  const [savingToggle, setSavingToggle] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
      const params = {};
      if (collectorId) params.employee_id = collectorId;
      if (status) params.status = status;

      const response = await axios.get(
        `${API_URL}/accounting/collection-requests/`,
        { headers: authHeader(), params },
      );

      setCollectors(
        Array.isArray(response.data.collectors)
          ? response.data.collectors
          : [],
      );
      setRequests(
        Array.isArray(response.data.requests)
          ? response.data.requests
          : [],
      );
    } catch (err) {
      setError(errorMessage(err, t("collectionRequests.loadFailed")));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [collectorId, status]);

  const selectedCollector = collectors.find(
    (c) => String(c.employee_id) === String(collectorId),
  );

  const toggleEnabled = async (enabled) => {
    if (!selectedCollector || savingToggle) return;

    setSavingToggle(true);
    setError("");

    try {
      await axios.post(
        `${API_URL}/accounting/collection-requests/collector/`,
        {
          employee_id: selectedCollector.employee_id,
          enabled,
        },
        { headers: authHeader() },
      );

      setCollectors((current) =>
        current.map((c) =>
          c.employee_id === selectedCollector.employee_id
            ? { ...c, free_collection_enabled: enabled }
            : c,
        ),
      );
    } catch (err) {
      setError(errorMessage(err, t("collectionRequests.actionFailed")));
    } finally {
      setSavingToggle(false);
    }
  };

  const decide = async (requestId, decision) => {
    if (processingId !== null) return;

    setProcessingId(requestId);
    setError("");

    try {
      await axios.post(
        `${API_URL}/accounting/collection-requests/decide/`,
        { request_id: requestId, decision },
        { headers: authHeader() },
      );
      await fetchData();
    } catch (err) {
      setError(errorMessage(err, t("collectionRequests.actionFailed")));
    } finally {
      setProcessingId(null);
    }
  };

  const statusLabel = (value) =>
    value === "accepted"
      ? t("collectionRequests.statusAccepted")
      : value === "refused"
        ? t("collectionRequests.statusRefused")
        : t("collectionRequests.statusPending");

  return (
    <div className="collection-requests-page" dir={direction}>
      <div className="collection-requests-heading">
        <h2>{t("collectionRequests.title")}</h2>
      </div>

      <div className="collection-requests-toolbar">
        <label className="collection-requests-field">
          <span>{t("collectionRequests.collector")}</span>
          <select
            value={collectorId}
            onChange={(e) => setCollectorId(e.target.value)}
          >
            <option value="">
              {t("collectionRequests.allCollectors")}
            </option>
            {collectors.map((c) => (
              <option key={c.employee_id} value={c.employee_id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <label className="collection-requests-field">
          <span>{t("collectionRequests.status")}</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="pending">
              {t("collectionRequests.statusPending")}
            </option>
            <option value="accepted">
              {t("collectionRequests.statusAccepted")}
            </option>
            <option value="refused">
              {t("collectionRequests.statusRefused")}
            </option>
            <option value="">{t("collectionRequests.statusAll")}</option>
          </select>
        </label>

        {selectedCollector && (
          <label className="collection-requests-switch">
            <input
              type="checkbox"
              checked={selectedCollector.free_collection_enabled === true}
              disabled={savingToggle}
              onChange={(e) => toggleEnabled(e.target.checked)}
            />
            <span>{t("collectionRequests.enabled")}</span>
          </label>
        )}
      </div>

      {error && (
        <div className="collection-requests-message is-error">{error}</div>
      )}

      {loading ? (
        <div className="collection-requests-message is-info">
          {t("collectionRequests.loading")}
        </div>
      ) : requests.length === 0 ? (
        <div className="collection-requests-message is-info">
          {t("collectionRequests.empty")}
        </div>
      ) : (
        <div className="collection-requests-grid">
          {requests.map((item) => {
            const client = isArabic
              ? item.client_name_ar || item.client_name_en
              : item.client_name_en;

            return (
              <div className="collection-requests-card" key={item.request_id}>
                <div className="collection-requests-card-top">
                  <span
                    className={`collection-requests-pill is-${item.status}`}
                  >
                    {statusLabel(item.status)}
                  </span>
                  <span className="collection-requests-invoice">
                    {t("collectionRequests.invoice", {
                      id: item.invoice_id,
                    })}
                  </span>
                </div>

                <p className="collection-requests-sentence">
                  <b>{item.collector_name}</b>{" "}
                  {t("collectionRequests.requestsRemove", {
                    amount: item.amount_usd,
                  })}{" "}
                  <b>{client}</b>
                </p>

                <div className="collection-requests-meta">
                  <div>{item.description}</div>
                  <div>
                    {t("collectionRequests.requestedBy")}{" "}
                    {item.requested_by} ·{" "}
                    {formatDateTime(item.requested_at)}
                  </div>
                  {item.status !== "pending" && (
                    <div>
                      {t("collectionRequests.decidedBy")}{" "}
                      {item.decided_by} ·{" "}
                      {formatDateTime(item.decided_at)}
                    </div>
                  )}
                </div>

                {item.status === "pending" && (
                  <div className="collection-requests-actions">
                    <button
                      type="button"
                      className="collection-requests-refuse"
                      disabled={processingId !== null}
                      onClick={() => decide(item.request_id, "refuse")}
                    >
                      {processingId === item.request_id
                        ? t("collectionRequests.processing")
                        : t("collectionRequests.refuse")}
                    </button>
                    <button
                      type="button"
                      className="collection-requests-accept"
                      disabled={processingId !== null}
                      onClick={() => decide(item.request_id, "accept")}
                    >
                      {processingId === item.request_id
                        ? t("collectionRequests.processing")
                        : t("collectionRequests.accept")}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CollectionRequests;
